import {
	BarChart3,
	Code,
	Lock,
	Play,
	Settings,
	Shield,
	Sparkles,
	TrendingUp,
	Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "../../context/AuthContext";
import { Login } from "./Login";
import { Signup } from "./Signup";

export function LandingAuth() {
	const { user, isLoading } = useAuth();
	const [, setLocation] = useLocation();
	const [isLogin, setIsLogin] = useState(true);

	useEffect(() => {
		if (!isLoading && user) {
			// Redirect if user is active and authorized
			const isAuthorized =
				user.status === "active" &&
				(user.role === "superAdmin" ||
					user.role === "admin" ||
					!user.approvalStatus ||
					user.approvalStatus === "approved");

			if (isAuthorized) {
				setLocation("/dashboard");
			}
		}
	}, [user, isLoading, setLocation]);

	return (
		<div className="h-screen w-screen overflow-hidden bg-gradient-to-b from-background via-background to-muted/30">
			{/* Apple-style subtle background effects */}
			<div className="fixed inset-0 overflow-hidden pointer-events-none">
				<div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] bg-[#FF6B35]/5 rounded-full blur-3xl animate-pulse" />
				<div className="absolute bottom-0 right-1/4 w-[40rem] h-[40rem] bg-[#FF8C42]/5 rounded-full blur-3xl animate-pulse [animation-delay:2s]" />
			</div>

			<div className="relative h-full flex flex-col">
				{/* Apple-style Header */}
				<header className="px-4 py-3 lg:px-8 lg:py-6 backdrop-blur-sm">
					<div className="flex items-center gap-3">
						<div className="flex items-center gap-2">
							<img
								src="/assets/novaroute-wordmark-light.svg"
								alt="Nova-route"
								className="h-7"
							/>
						</div>
						<div className="h-6 w-px bg-border/40" />
						<p className="text-sm font-medium text-foreground/60">
							Configure Models, Test in Playground, Use SDK
						</p>
					</div>
				</header>

				{/* Main Content - Centered, no scroll */}
				<main className="flex-1 flex items-start justify-center px-4 lg:px-8 pt-0 lg:pt-2">
					<div className="w-full max-w-7xl">
						{/* Hero Section (Mobile) */}
						<div className="mb-2 text-center lg:hidden">
							<div className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary border border-primary/20">
								<Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
								<span>
									5-Minute Setup • Instant Testing • One-Line Integration
								</span>
							</div>
							<h2 className="text-3xl font-bold tracking-tight text-foreground mb-2 leading-tight">
								You Configure, We Optimize
								<br />
								<span className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] bg-clip-text text-transparent">
									AI Infrastructure Made Simple
								</span>
							</h2>
							<p className="text-base text-muted-foreground leading-relaxed">
								Set up your AI models in minutes with our 5-step wizard. Test
								instantly in the playground—no projects or API keys needed.
								Integrate with one line of code. Nova-route handles caching,
								optimization, and cost reduction automatically.
							</p>
						</div>

						{/* Desktop Layout: Hero + Auth Side-by-Side */}
						<div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
							{/* Hero Section (Desktop) */}
							<div className="hidden lg:block space-y-6">
								<div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary border border-primary/20">
									<Sparkles className="h-4 w-4" strokeWidth={2} />
									<span>
										5-Minute Setup • Instant Testing • One-Line Integration
									</span>
								</div>
								<h2 className="text-5xl font-bold tracking-tight text-foreground leading-tight lg:text-6xl">
									You Configure, We Optimize
									<br />
									<span className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] bg-clip-text text-transparent">
										AI Infrastructure Made Simple
									</span>
								</h2>
								<p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
									Set up your AI models in minutes with our 5-step wizard. Test
									instantly in the playground—no projects or API keys needed.
									Integrate with one line of code. Nova-route handles caching,
									optimization, and cost reduction automatically.
								</p>
								{/* Three Pillars */}
								<div className="space-y-6">
									{/* Pillar 1: Model Configuration */}
									<div className="flex items-start gap-4 text-foreground">
										<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF6B35]/10 flex-shrink-0">
											<Settings
												className="h-6 w-6 text-[#FF6B35]"
												strokeWidth={2}
											/>
										</div>
										<div className="flex-1">
											<h3 className="text-lg font-semibold mb-1">
												5-Step Model Configuration
											</h3>
											<p className="text-sm text-muted-foreground leading-relaxed">
												Connect any AI provider in minutes. Our wizard guides
												you through provider selection, connection testing,
												model selection, and parameter tuning. Your credentials
												are encrypted and secure.
											</p>
										</div>
									</div>

									{/* Pillar 2: Playground */}
									<div className="flex items-start gap-4 text-foreground">
										<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF8C42]/10 flex-shrink-0">
											<Play
												className="h-6 w-6 text-[#FF8C42]"
												strokeWidth={2}
											/>
										</div>
										<div className="flex-1">
											<h3 className="text-lg font-semibold mb-1">
												Test Instantly, No Setup Required
											</h3>
											<p className="text-sm text-muted-foreground leading-relaxed">
												Try your configurations immediately in the playground.
												No projects or API keys needed—just select your model
												config and start chatting. See cache hits, token usage,
												and savings in real-time.
											</p>
										</div>
									</div>

									{/* Pillar 3: SDK Integration */}
									<div className="flex items-start gap-4 text-foreground">
										<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500/10 flex-shrink-0">
											<Code
												className="h-6 w-6 text-green-400"
												strokeWidth={2}
											/>
										</div>
										<div className="flex-1">
											<h3 className="text-lg font-semibold mb-1">
												One-Line Integration
											</h3>
											<p className="text-sm text-muted-foreground leading-relaxed">
												Drop-in replacement for OpenAI. Just change your base
												URL—everything else stays the same. Works with OpenAI
												SDK, official Nova SDK, or any HTTP client. Nova handles
												caching, routing, and optimization automatically.
											</p>
										</div>
									</div>
								</div>

								{/* Heavy Lifting Section */}
								<div className="pt-6 border-t border-border/50">
									<h3 className="text-2xl font-bold text-foreground mb-2">
										We Handle the Heavy Lifting
									</h3>
									<p className="text-base text-muted-foreground mb-6">
										Focus on building, we'll optimize your AI costs
									</p>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div className="flex items-start gap-3">
											<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF6B35]/10 flex-shrink-0 mt-0.5">
												<Zap
													className="h-4 w-4 text-[#FF6B35]"
													strokeWidth={2}
												/>
											</div>
											<div>
												<p className="text-sm font-medium text-foreground">
													Multi-Tier Caching
												</p>
												<p className="text-xs text-muted-foreground">
													Hot cache for exact matches, semantic cache for
													similar queries
												</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF8C42]/10 flex-shrink-0 mt-0.5">
												<TrendingUp
													className="h-4 w-4 text-[#FF8C42]"
													strokeWidth={2}
												/>
											</div>
											<div>
												<p className="text-sm font-medium text-foreground">
													Cost Optimization
												</p>
												<p className="text-xs text-muted-foreground">
													Automatic token savings tracking and reporting
												</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500/10 flex-shrink-0 mt-0.5">
												<Shield
													className="h-4 w-4 text-green-400"
													strokeWidth={2}
												/>
											</div>
											<div>
												<p className="text-sm font-medium text-foreground">
													Provider Management
												</p>
												<p className="text-xs text-muted-foreground">
													Connection pooling, retry logic, error handling
												</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 flex-shrink-0 mt-0.5">
												<BarChart3
													className="h-4 w-4 text-blue-400"
													strokeWidth={2}
												/>
											</div>
											<div>
												<p className="text-sm font-medium text-foreground">
													Analytics
												</p>
												<p className="text-xs text-muted-foreground">
													Real-time metrics on usage, savings, and performance
												</p>
											</div>
										</div>
										<div className="flex items-start gap-3 md:col-span-2">
											<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/10 flex-shrink-0 mt-0.5">
												<Lock
													className="h-4 w-4 text-purple-400"
													strokeWidth={2}
												/>
											</div>
											<div>
												<p className="text-sm font-medium text-foreground">
													Security
												</p>
												<p className="text-xs text-muted-foreground">
													Encrypted credentials, secure API keys, access control
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Auth Forms - Unified, no scroll */}
							<div className="flex justify-center">
								<div className="w-full max-w-md rounded-3xl border border-border/50 bg-card p-8 shadow-xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl">
									<h3 className="text-2xl font-bold tracking-tight text-foreground mb-6">
										{isLogin ? "Sign In" : "Create Account"}
									</h3>
									{isLogin ? (
										<Login onRegisterClick={() => setIsLogin(false)} />
									) : (
										<Signup onLoginClick={() => setIsLogin(true)} />
									)}
								</div>
							</div>
						</div>
					</div>
				</main>

				{/* Footer */}
				<footer className="p-6 text-center text-xs font-medium text-muted-foreground">
					<p>
						🔒 All signups require admin approval • Please wait for activation
					</p>
					<p className="mt-2 text-xs text-muted-foreground/70">
						Model Configuration • Playground Testing • SDK Integration
					</p>
					<p className="mt-1 text-xs text-muted-foreground/60">
						OpenAI-compatible • Drop-in replacement • No code changes required
					</p>
				</footer>
			</div>
		</div>
	);
}
